/**
 * RequestAccess — shown when a signed-in user has no active role yet
 * (ADR-0015). They pick a role + justification; a supervisor/admin approves.
 * Standalone (no AppShell) — the user has no access to the app yet.
 */
import { useState, type FormEvent } from 'react';
import { Clock, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { RoleSchema, type Role } from '@vari-crew/schemas';
import { useMyAccess, useSubmitAccessRequest } from '@/api/access';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/Button';

const ROLE_LABELS: Record<Role, string> = {
  pm: 'Project Manager — request & track work',
  supervisor: 'Crew Supervisor — schedule & dispatch',
  crew: 'Crew Member — field work',
  sales: 'Sales — project recaps',
  admin: 'Admin — full access',
};

export function RequestAccess() {
  const { data: access, isLoading } = useMyAccess();
  const submit = useSubmitAccessRequest();
  const { signOut } = useAuth();
  const [role, setRole] = useState<Role>('pm');
  const [justification, setJustification] = useState('');

  const status = access?.status ?? 'none';
  const requested = access?.requested_role ?? null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit.mutate(
      { requested_role: role, justification: justification.trim() || undefined },
      {
        onSuccess: () => toast.success('Access request submitted'),
        onError: (err) => toast.error(err.message),
      },
    );
  }

  return (
    <div className="min-h-full grid place-items-center bg-fill-soft p-6">
      <div className="max-w-md w-full bg-bg border border-line rounded-xl p-8 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-ink text-bg grid place-items-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-tight">Vari Field Management System</h1>
            <p className="text-xs text-ink-faint truncate">
              {access?.display_name ?? 'Welcome'}
              {access?.email ? ` · ${access.email}` : ''}
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="py-8 grid place-items-center">
            <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
          </div>
        ) : status === 'pending' ? (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 font-medium">
              <Clock className="w-4 h-4 text-status-enroute-fg" /> Request awaiting approval
            </div>
            <p className="text-ink-dim">
              Your request{requested ? ` for ${ROLE_LABELS[requested]}` : ''} has been sent to a
              Vari admin. You'll get in as soon as it's approved — try again shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {status === 'denied' && (
              <div className="flex items-start gap-2 text-sm rounded-md bg-status-blocked text-status-blocked-fg px-3 py-2">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Your previous request was declined. You can submit a new one.
              </div>
            )}
            <p className="text-sm text-ink-dim">
              You're signed in, but don't have access yet. Tell us what you need.
            </p>
            <label className="block text-sm space-y-1">
              <span className="text-ink-dim">Role</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className="w-full px-3 py-2 border border-line rounded-md bg-bg text-sm"
              >
                {RoleSchema.options.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm space-y-1">
              <span className="text-ink-dim">
                Justification <span className="text-ink-faint">(optional)</span>
              </span>
              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={3}
                placeholder="e.g. I'm the PM for the Grapevine installs"
                className="w-full px-3 py-2 border border-line rounded-md bg-bg text-sm resize-none"
              />
            </label>
            <Button type="submit" className="w-full" disabled={submit.isPending}>
              {submit.isPending ? 'Submitting…' : 'Request access'}
            </Button>
          </form>
        )}

        <button
          onClick={() => void signOut()}
          className="block w-full text-center text-xs text-ink-faint hover:text-ink"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
